import React, { forwardRef, ReactNode, useRef } from "react";
// import HCaptcha from "@hcaptcha/react-hcaptcha";
export type FormErrorState = Record<string, string[] | undefined> | null;
/* jscpd:ignore-start */
import { customSanitize } from "@/utils/customSanitize";
import { ListPropsV2, ListWithTextInput } from "@/components/Listbox/Listbox";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import { ImageType } from "@/wp/types/acf-header-types";

import styles from "../Form.module.scss";

type CommonProps = {
  label?: string;
  errors?: FormErrorState;
  required_label?: string;
};

type InputProps = {
  children?: React.ReactNode;
  subline?: string;
  inputType:
    | "number"
    | "text"
    | "checkbox"
    | "tel"
    | "url"
    | "email"
    | "textarea"
    | "date";
  label_checkbox?: string;
  required_label?: string;
  checked?: boolean;
} & CommonProps &
  React.InputHTMLAttributes<HTMLInputElement>;

interface LabelProps {
  Tag?: "label" | "p" | "legend";
  label?: string;
  required: boolean | undefined;
  required_label?: string;
  htmlFor?: string;
}

export const LabelV2 = ({
  label,
  htmlFor,
  required_label,
  required,
  Tag = "label",
}: LabelProps) => {
  return (
    <Tag className={`label`} htmlFor={htmlFor}>
      {label && label}
      {required && required_label && (
        <span className={"required-text"}> {required_label}</span>
      )}
    </Tag>
  );
};

// TODO: extend this component to support more input types
// This component is currently used for inputs with type: text, checkbox, email, textarea
export const InputV2 = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputProps
>(
  (
    {
      errors,
      label,
      subline,
      inputType,
      required_label,
      required,
      className = "",
      ...props
    },
    ref,
  ) => {
    const id = `input-${props.name}`;
    return (
      <FieldWrapper
        errors={errors}
        name={props.name}
        className={`w-full ${className} ${styles["input-group-formbuilder"]}`}
      >
        <LabelV2
          label={label}
          required={required}
          required_label={required_label}
          htmlFor={id}
        />
        {(inputType === "text" ||
          inputType === "email" ||
          inputType === "number" ||
          inputType === "tel" ||
          inputType === "url" ||
          inputType === "date") && (
          <>
            <input
              id={id}
              {...props}
              type={inputType}
              ref={ref as React.Ref<HTMLInputElement>}
              className={`${styles["input"]} placeholder`}
              required={required}
            />
          </>
        )}

        {subline && <span className={styles["subline"]}>{subline}</span>}
      </FieldWrapper>
    );
  },
);

InputV2.displayName = "InputV2";

type TextAriaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  CommonProps;

export const TextAreaV2 = ({
  errors,
  label,
  required_label,
  required,
  className = "",
  ...props
}: TextAriaProps) => {
  const id = `input-${props.name}`;

  return (
    <FieldWrapper
      errors={errors}
      name={props.name}
      className={`w-full ${className}`}
    >
      <LabelV2
        label={label}
        required={required}
        required_label={required_label}
        htmlFor={id}
      />
      <textarea
        {...props}
        id={id}
        className={`${styles["input"]} placeholder`}
        required={required}
      />{" "}
      <span className={styles["bar-text-area"]}></span>
    </FieldWrapper>
  );
};

export const CheckboxV2 = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      errors,
      label,
      required_label,
      required,
      label_checkbox,
      className = "",
      inputType: _ /* otherwise it gets passed onto the input */,
      checked,
      ...props
    },
    ref,
  ) => {
    const id = `input-` + props.name;
    const checkedProps = typeof checked === "boolean" ? { checked } : {};

    return (
      <FieldWrapper
        errors={errors}
        name={props.name}
        className={`${className}`}
      >
        <fieldset>
          <LabelV2
            Tag="legend"
            label={label}
            required={required}
            required_label={required_label}
          />
          <div className={`${styles["checkbox-wrapper"]}`}>
            <div className={styles[`input-checkbox`]}>
              <input
                {...props}
                {...checkedProps}
                ref={ref as React.Ref<HTMLInputElement>}
                type="checkbox"
                id={id}
                required={required}
              />
              {label_checkbox && (
                /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
                <label
                  dangerouslySetInnerHTML={{
                    __html: customSanitize(label_checkbox),
                  }}
                  htmlFor={id}
                  className="mb-0 pb-0"
                />
              )}
            </div>
          </div>
        </fieldset>
      </FieldWrapper>
    );
  },
);

CheckboxV2.displayName = "CheckboxV2";

type RadioProps = React.InputHTMLAttributes<HTMLInputElement> &
  CommonProps & {
    options: {
      label: string;
      value: string;
      checked?: boolean;
    }[];
  };

export const RadioV2 = ({
  options,
  errors,
  label,
  className,
  required_label,
  required,
  ...props
}: RadioProps) => {
  return (
    <FieldWrapper errors={errors} name={props.name} className={`${className}`}>
      <LabelV2
        Tag="p"
        label={label}
        required={required}
        required_label={required_label}
      />

      <div className={styles["input-wrapper"]}>
        {options.map((o) => {
          const radioId = `${props.name}-${o.value}`;

          return (
            <div
              className={`${styles["input-radiobutton"]} is-flex is-align-items-center mb-2`}
              key={radioId}
            >
              <input
                {...props}
                checked={o.checked}
                onChange={(e) => {
                  props.onChange?.(e);
                }}
                type="radio"
                id={radioId}
                value={o.value}
              />
              <label
                className={`${styles["label"]} has-text-font-regular mb-0 pb-0`}
                htmlFor={radioId}
              >
                {o.label}
              </label>
            </div>
          );
        })}
      </div>
    </FieldWrapper>
  );
};

RadioV2.displayName = "RadioV2";

type SelectProps = CommonProps &
  React.InputHTMLAttributes<HTMLSelectElement> & {
    errors?: FormErrorState;
    children?: React.ReactNode;
    items: ListPropsV2["items"];
    setSelected: ListPropsV2["setSelected"];
    selected?: string;
    placeholder?: string;
  };

export const SelectV2 = ({
  className,
  errors,
  items,
  selected,
  setSelected,
  label,
  required_label,
  required,
  placeholder,
  ...props
}: SelectProps) => {
  const id = "select-" + props.name;
  return (
    <FieldWrapper errors={errors} name={props.name} className={`${className}`}>
      <LabelV2
        htmlFor={id}
        label={label}
        required={required}
        required_label={required_label}
      />
      {/* <ListV2
        className="w-100"
        buttonId={id}
        items={items}
        setSelected={setSelected}
        selected={selected}
        placeholder={placeholder}
      /> */}
      <ListWithTextInput
        className="w-100"
        buttonId={id}
        items={items}
        setSelected={setSelected}
        selected={selected}
        placeholder={placeholder}
      />
    </FieldWrapper>
  );
};

interface FieldWrapperProps {
  errors: FormErrorState | undefined;
  name: string | undefined;
  children: ReactNode;
  className?: string;
}

export const FieldWrapper = ({
  errors,
  name,
  children,
  className,
}: FieldWrapperProps) => {
  const hasError = errors && name && errors[name];

  return (
    <div
      data-field-name={name}
      className={`${styles["input"]} ${hasError ? "has-error" : ""} ${className}`}
    >
      {children}
      {hasError && (
        <p className={styles.error}>
          {(errors?.[name!]?.filter(Boolean) ?? []).join(", ")}
        </p>
      )}
    </div>
  );
};
type FileUploadProps = CommonProps & {
  name: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  required_label?: string;
  labelUploadButton: string;
  ariaLabelUploadButton: string;
  imageUploadButton: ImageType | false;
  labelRemoveButton: string;
  ariaLabelRemoveButton: string;
  imageRemoveButton: ImageType | false;
  file: File | null;
  onFileChange: (file: File | null) => void;
};

export const FileUploadV2 = ({
  errors,
  label,
  required_label,
  required,
  className = "",
  name,
  disabled,
  labelUploadButton,
  ariaLabelUploadButton,
  imageUploadButton,
  labelRemoveButton: _labelRemoveButton,
  ariaLabelRemoveButton,
  imageRemoveButton,
  file,
  onFileChange,
}: FileUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const id = `input-${name}`;

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] ?? null;
    onFileChange(selectedFile);
  };

  const handleRemove = () => {
    onFileChange(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <FieldWrapper errors={errors} name={name} className={className}>
      <LabelV2
        label={label}
        required={required}
        required_label={required_label}
        htmlFor={id}
      />
      <div className={styles["file-upload-wrapper"]}>
        <button
          type="button"
          className={styles["file-upload-button"]}
          onClick={handleUploadClick}
          aria-label={ariaLabelUploadButton}
          disabled={disabled}
        >
          <span className={styles["file-upload-icon"]}>
            {imageUploadButton ? (
              <CustomSVG
                src={imageUploadButton.url}
                width={27}
                height={27}
                aria-hidden="true"
              />
            ) : (
              <svg
                width="11"
                height="15"
                viewBox="0 0 11 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M5.5 13V2M5.5 2L1 6.5M5.5 2L10 6.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
          <span className={styles["file-upload-label"]}>
            {file ? file.name : labelUploadButton}
          </span>
        </button>

        {file && (
          <button
            type="button"
            className={styles["file-remove-button"]}
            onClick={handleRemove}
            aria-label={ariaLabelRemoveButton}
            disabled={disabled}
          >
            {imageRemoveButton ? (
              <CustomSVG
                src={imageRemoveButton.url}
                width={27}
                height={27}
                aria-hidden="true"
              />
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        id={id}
        type="file"
        name={name}
        accept=".pdf,application/pdf"
        onChange={handleFileSelected}
        className={styles["file-input-hidden"]}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      />
    </FieldWrapper>
  );
};

FileUploadV2.displayName = "FileUploadV2";
/* jscpd:ignore-end */
